import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import OfferPlatformTable from './OfferPlatformTable';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { OptionalItemsTable } from './OptionalItemsTable';
import { AdditionalItemsTable } from './AdditionalItemsTable';
import { Form } from 'react-bootstrap';
import _debounce from 'lodash/debounce';
import { setPersonalOfferDescription, setInviteAttachment } from '../../../../../../../../store/brand/offerBrandSlice';
import { OfferHeader } from './OfferHeader';
import { ProposalReviewInfo } from './ProposalReviewInfo';
import { DeclineOffer } from '../../../../../../../influencer/offer/modal/offer-modal/tabs/offer/DeclineOffer';
import { Totals } from './Totals';
import {
    downloadFile,
    formatDate,
    getGrandTotal,
    getSubtotalAdditionalItems,
} from '../../../../../../../../shared/functions/Functions';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import { TotalTypes } from '../../../../../../../../enum/TotalTypes';
import { AdditionalItemToggle } from './AdditionalItemToggle';
import ItemValueSection from '../../../../../../../../shared/components/offer/ItemValueSection';
import { UploadImage } from '../../../../../../../../shared/components/UploadImage';
import FilePlaceholder from '../../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../../shared/components/Fancybox';
import { getTruncatedData } from '../../../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../../../shared/components/tooltip/Tooltip';
import {PackageIncludedToggle} from './PackageIncludedToggle';
import { BookingService } from '../../../../service';
import { ErrorToast } from '../../../../../../../../utils/toasters';

export const Offer = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        campaign,
        initialOffer,
        offerModalStatus,
        isTransparentPricing,
        isCharity,
        isEditable,
        canToggleAddons,
        inviteAttachment,
    } = useSelector((state: IAllStates) => state.offerBrand);

    const isNegotiate = offerModalStatus === 'negotiate';
    const feePercentage = Math.round((1 - Number(campaign?.feePercentage ?? 0.0)) * 100);
    const subTotalSum = initialOffer.isAddonsOnly
        ? getSubtotalAdditionalItems(initialOffer.distribution)
        : getGrandTotal(initialOffer.offerItems, initialOffer.distribution);
    const totalLiveFeeSum = Math.ceil(subTotalSum / (campaign?.feePercentage ?? 1)) - subTotalSum;
    const transparentGrandTotal = isNegotiate
        ? Math.ceil(subTotalSum / (campaign?.feePercentage ?? 1))
        : campaign?.offers[0]?.totalWithFee ?? 0;
    const legalDocument = campaign?.offers[0]?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        20
    );
    const displayFiles =
        inviteAttachment !== undefined
            ? inviteAttachment
            : offerModalStatus === 'negotiate' && legalDocument
            ? [
                  {
                      name: legalDocument.originalName ?? legalDocument.name,
                      type: legalDocument.mime,
                  } as unknown as Blob,
              ]
            : undefined;

    useEffect(() => {
        if (offerModalStatus === 'negotiate' && legalDocument && inviteAttachment === undefined) {
            dispatch(
                setInviteAttachment([
                    {
                        name: legalDocument.originalName ?? legalDocument.name,
                        type: legalDocument.mime,
                    } as unknown as Blob,
                ])
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerModalStatus, legalDocument?.id]);

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        dispatch(setPersonalOfferDescription(value));
    }, 300);

    const saveInviteFile: React.Dispatch<React.SetStateAction<Blob[] | MediaSource[] | undefined>> = value => {
        const resolvedFiles = typeof value === 'function' ? value(inviteAttachment) : value;
        const normalizedFiles = Array.isArray(resolvedFiles)
            ? resolvedFiles
            : resolvedFiles
            ? [resolvedFiles]
            : undefined;
        dispatch(setInviteAttachment(normalizedFiles as Blob[] | MediaSource[] | undefined));
    };

    const savePurchaseOrder = async (value: string): Promise<boolean> => {
        try {
            await BookingService.updatePurchaseOrder(campaign?.offers[0]?.id as number, value);
            return true;
        } catch (e) {
            ErrorToast(e);
            return false;
        }
    };

    return (
        <>
            <DeclineOffer
                declineReason={campaign?.offers[0]?.declineReason}
                declineComment={campaign?.offers[0]?.declineComment}
                status={campaign?.offers[0]?.status}
                cancellationNote={campaign?.offers[0]?.cancellationNote}
                cancellationDocumentName={campaign?.offers[0]?.cancellationDocumentName}
                cancellationDocumentPath={campaign?.offers[0]?.cancellationDocumentPath}
                cancellationDocumentUploadedAt={campaign?.offers[0]?.cancellationDocumentUploadedAt}
            />
            <OfferHeader
                createdAt={campaign?.offers[0]?.createdAt ?? ''}
                offerItemId={campaign?.offers[0]?.id ?? 0}
                isEditable={isEditable}
                isRequested={campaign?.offers[0]?.isRequested}
                isApproved={campaign?.offers[0]?.isApproved}
                isDeclined={campaign?.offers[0]?.isDeclined}
                offerModalStatus={offerModalStatus}
                acceptedDate={campaign?.offers[0]?.acceptedDate}
                status={campaign?.offers[0]?.status}
                purchaseOrderInfluencer={campaign?.offers[0]?.purchaseOrderInfluencer}
                onSavePurchaseOrder={savePurchaseOrder}
            />
            <ProposalReviewInfo
                isApproved={campaign?.offers[0]?.isApproved}
                isDeclined={campaign?.offers[0]?.isDeclined}
                isConvertedToOffer={campaign?.offers[0]?.isConvertedToOffer}
                proposalDeclineReason={campaign?.offers[0]?.proposalDeclineReason}
                proposalReviewedBy={campaign?.offers[0]?.proposalReviewedBy}
                updatedAt={campaign?.offers[0]?.updatedAt}
            />
            <PackageIncludedToggle/>
            {canToggleAddons && <AdditionalItemToggle offerItemId={campaign?.spendingOffer?.id ?? 0} />}
            <div className="py-2">
                {!initialOffer.isAddonsOnly && <OfferPlatformTable />}
                <AdditionalItemsTable />
                {offerModalStatus === 'negotiate' &&
                    campaign?.offerPlaceholder?.distribution &&
                    campaign?.offerPlaceholder?.distribution?.length > 0 && <OptionalItemsTable />}
                {isTransparentPricing && !isCharity && !initialOffer.isAddonsOnly && (
                    <div className="w-100 mt-2">
                        <Totals title={TotalTypes.SUBTOTAL} price={subTotalSum} feePercentage={100 - feePercentage} />
                    </div>
                )}
                {isTransparentPricing ? (
                    <div className="w-100 mt-2">
                        <Totals
                            title={TotalTypes.FEE}
                            price={
                                offerModalStatus === 'negotiate' ? totalLiveFeeSum : campaign?.offers[0]?.feeAmount ?? 0
                            }
                            feePercentage={feePercentage}
                            displayPlus={true}
                        />
                    </div>
                ) : (
                    <div className="my-3"></div>
                )}
                {isTransparentPricing && !isCharity && (
                    <div className="w-100 mt-2">
                        <Totals title={TotalTypes.GRAND} price={transparentGrandTotal} feePercentage={100} />
                    </div>
                )}

                {isCharity && !!campaign?.charityValueDesc && (
                    <ItemValueSection classes="px-1" description={campaign?.charityValueDesc} />
                )}

                {offerModalStatus === 'negotiate' && (
                    <>
                        <h6 className="mt-3">{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Control
                                    onChange={changeHandler}
                                    defaultValue={initialOffer.personalOffer}
                                    as="textarea"
                                    name="personalOffer"
                                    rows={5}
                                    placeholder={t('influencer.offer_management.modal.add_personal_offer_placeholder')}
                                />
                            </div>
                            <div className="col-md-6 mt-3 mt-md-0">
                                <div className="offer-dropzone">
                                    <UploadImage
                                        saveFile={saveInviteFile}
                                        isMultiple={false}
                                        initialFiles={displayFiles}
                                        isFullHeight={true}
                                        acceptedFormats="application/pdf"
                                        centerText={true}
                                        fileName={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {offerModalStatus !== 'negotiate' && campaign?.offers[0]?.personalOffer && (
                    <div className="mt-4 px-2">
                        <div>
                            <h6>{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        </div>
                        <p className="text-muted mb-0">{campaign?.offers[0]?.personalOffer}</p>
                    </div>
                )}
                {offerModalStatus !== 'negotiate' && campaign?.offers[0]?.legalDocuments?.[0] && (
                    <div className="mt-3 px-1">
                        <div
                            className="d-flex align-items-start p-2 border rounded position-relative"
                            style={{ maxWidth: 300 }}
                        >
                            <Fancybox>
                                <FilePlaceholder
                                    file={campaign?.offers[0]?.legalDocuments?.[0]}
                                    hideText={true}
                                    classIcon="font-22"
                                    styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                    nameFancybox="legal-document"
                                    handleDownload={() =>
                                        downloadFile(
                                            campaign?.offers[0]?.legalDocuments?.[0]?.path ?? '',
                                            campaign?.offers[0]?.legalDocuments?.[0]?.originalName
                                        )
                                    }
                                    right={-10}
                                />
                            </Fancybox>
                            <div className="ms-2">
                                <TooltipOverlay content={isFileNameTruncated ? legalDocument?.originalName : ''}>
                                    <p
                                        className="mb-0 fw-semibold cursor-pointer"
                                        onClick={() =>
                                            NativeFancybox.show([
                                                {
                                                    src: campaign?.offers[0]?.legalDocuments?.[0]?.path,
                                                    type: 'iframe',
                                                },
                                            ])
                                        }
                                    >
                                        {truncatedFileName || '-'}
                                    </p>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                    <TooltipOverlay
                                        content={isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''}
                                        dFlex={false}
                                    >
                                        <span>{truncatedUploaderName || '-'}</span>
                                    </TooltipOverlay>
                                </p>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                    {formatDate(campaign?.offers[0]?.legalDocuments?.[0]?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Offer;
